import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { twJoin } from "tailwind-merge";

import { Button } from "@/design-system/Button";
import { IconButton } from "@/design-system/IconButton";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useBool } from "@/hooks/useBool";
import { useElementHeight } from "@/hooks/useElementHeight";
import { askNobiChatMutations } from "@/queries/ask-nobi/mutations";
import { useTranslation } from "@/translations";
import { Dialog as ArkDialog, Portal as ArkPortal } from "@/ui-adapters/ArkDialog";
import { RuntimeBottomPadding } from "@/ui-adapters/RuntimeBottomPadding";

import { ThumbsDownIcon } from "../Icons/ThumbsDownIcon";
import { ThumbsUpIcon } from "../Icons/ThumbsUpIcon";
import { XCloseIcon } from "../Icons/XCloseIcon";
import { AskNobiChatFeed } from "./AskNobiChatFeed";
import { AskNobiChatInput } from "./AskNobiChatInput";
import { AskNobiChatInputNotice } from "./AskNobiChatInputNotice";
import { AskNobiChatLogo } from "./AskNobiChatLogo";
import { AskNobiNegativeFeedbackModal } from "./AskNobiNegativeFeedbackModal";
import { AskNobiSuggestionButton } from "./AskNobiSuggestionButton";
import type { UseAskNobiChatResult } from "./helpers/useAskNobiChat";

const FORCED_FEEDBACK_THRESHOLD = 5;

type FeedbackState = "visible" | "submitted" | "dismissed";

interface AskNobiChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  chat: UseAskNobiChatResult;
  suggestions?: string[];
}

export function AskNobiChatDrawer({ isOpen, onClose, chat, suggestions }: AskNobiChatDrawerProps): React.ReactNode {
  const { t } = useTranslation();
  const postHog = usePostHog();
  const { data: linkedProjects } = useLinkedProjects();
  const [inputValue, setInputValue] = useState("");
  const [footerRef, footerHeight] = useElementHeight<HTMLDivElement>();
  const [feedbackState, setFeedbackState] = useState<FeedbackState>("visible");
  const [enrichmentModalOpen, enrichmentModalOpenHandlers] = useBool();
  const [closedSingleProjectBanner, closedSingleProjectBannerHandlers] = useBool();
  const rateConversation = askNobiChatMutations.useRateConversation();

  // Reset feedback when starting a new conversation
  const conversationId = chat.conversationId;
  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setFeedbackState("visible");
    enrichmentModalOpenHandlers.setFalse();
  }, [conversationId, enrichmentModalOpenHandlers]);

  // Auto-dismiss the submitted banner after 5 seconds
  useEffect(() => {
    if (feedbackState !== "submitted") {
      return;
    }

    const timer = setTimeout(() => setFeedbackState("dismissed"), 5000);

    return () => clearTimeout(timer);
  }, [feedbackState]);

  async function handleSend() {
    const message = inputValue.trim();
    if (message) {
      setInputValue("");
      await chat.sendMessage(message);
    }
  }

  const { sendMessage } = chat;
  const handleSuggestion = useCallback(
    (suggestion: string) => {
      postHog.capture("clicked_ask_nobi_suggestion", { page: "ask_nobi_chat_drawer", suggestion });
      void sendMessage(suggestion);
    },
    [sendMessage, postHog],
  );

  const userMessageCount = chat.messages.filter((m) => m.sender === "user").length;
  const showFeedbackBanner =
    !!conversationId && feedbackState === "visible" && userMessageCount >= FORCED_FEEDBACK_THRESHOLD;

  async function handleBannerRate(rating: "positive" | "negative") {
    if (!conversationId) {
      return;
    }

    if (rating === "negative") {
      enrichmentModalOpenHandlers.setTrue();
    }

    await rateConversation.mutateAsync({ conversationId, data: { rating } });

    if (rating === "positive") {
      setFeedbackState("submitted");
    }
  }

  let topBar: React.ReactNode = undefined;
  if (feedbackState === "submitted") {
    topBar = (
      <AskNobiChatInputNotice
        variant="success"
        text={t("component.ask-nobi-chat.feedback-banner.submitted")}
        onClose={() => setFeedbackState("dismissed")}
      />
    );
  } else if (showFeedbackBanner) {
    topBar = (
      <AskNobiChatInputNotice
        variant="special"
        text={t("component.ask-nobi-chat.feedback-banner.question")}
        actions={
          <>
            <Button
              disabled={rateConversation.isPending}
              onClick={() => handleBannerRate("positive")}
              data-testid="ai-chat-feedback-banner-good-btn"
              size="sm"
              variant="ghostSecondary"
              icon={<ThumbsUpIcon />}
            >
              {t("component.ask-nobi-chat.feedback-banner.good")}
            </Button>
            <Button
              disabled={rateConversation.isPending}
              onClick={() => handleBannerRate("negative")}
              data-testid="ai-chat-feedback-banner-not-great-btn"
              size="sm"
              variant="ghostSecondary"
              icon={<ThumbsDownIcon />}
            >
              {t("component.ask-nobi-chat.feedback-banner.not-great")}
            </Button>
          </>
        }
      />
    );
  } else {
    const hasMultipleProjects = (linkedProjects?.length ?? 0) > 1;
    const isSingleProjectFiltered = (chat.filters?.projectIds?.length ?? 0) === 1;

    if (hasMultipleProjects && isSingleProjectFiltered && !closedSingleProjectBanner) {
      topBar = (
        <AskNobiChatInputNotice
          variant="special"
          text={t("component.ask-nobi-chat-drawer.context-banner.single-project")}
          onClose={() => closedSingleProjectBannerHandlers.setTrue()}
        />
      );
    }
  }

  const filteredSuggestions = suggestions?.filter((s) => s && s !== "-");
  const hasSuggestions = filteredSuggestions && filteredSuggestions.length > 0;
  const showEmptyState = chat.messages.length === 0 && hasSuggestions;

  return (
    <>
      <ArkDialog.Root
        open={isOpen}
        onOpenChange={(details) => {
          if (!details.open) onClose();
        }}
        lazyMount
        unmountOnExit
        closeOnInteractOutside={false}
        closeOnEscape={false}
      >
        <ArkPortal>
          <ArkDialog.Backdrop
            className={twJoin(
              "fixed top-0 left-0 z-backdrop size-full cursor-pointer bg-background-default-inverse/60 drawer-motion",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out",
              "data-[state=open]:animate-in data-[state=open]:fade-in",
            )}
          />
          <ArkDialog.Positioner className="fixed right-0 bottom-0 z-overlay h-fit max-h-dvh w-full md:top-0 md:h-full md:w-fit">
            <ArkDialog.Content
              className={twJoin(
                "ease @container flex drawer-compact-height max-h-dvh w-full flex-col overflow-hidden rounded-t-lg nobi-drawer-background drawer-motion outline-none md:h-full md:drawer-wide-width md:rounded-l-lg md:rounded-tr-none",
                "data-[state=open]:animate-in max-md:data-[state=open]:slide-in-from-bottom md:data-[state=open]:slide-in-from-right",
                "data-[state=closed]:animate-out max-md:data-[state=closed]:slide-out-to-bottom md:data-[state=closed]:slide-out-to-right",
              )}
            >
              {/* Header */}
              <header className="z-raised flex shrink-0 items-center justify-between gap-space-md border-b-hairline border-border-default px-space-lg py-space-md">
                <ArkDialog.Title asChild>
                  <Inline gap="sm" align="center" wrap={false}>
                    <AskNobiChatLogo size="sm" animateOnHover />
                    <span className="text-heading-3 text-text-brand-secondary">
                      {t("component.ask-nobi-chat-drawer.title")}
                    </span>
                  </Inline>
                </ArkDialog.Title>
                <ArkDialog.CloseTrigger asChild>
                  <IconButton
                    title={t("common.action.close")}
                    withTooltip={false}
                    onClick={onClose}
                    icon={<XCloseIcon />}
                    isCircular
                    size="sm"
                    variant="soft"
                  />
                </ArkDialog.CloseTrigger>
              </header>

              {/* Messages + overlaid footer */}
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <div className="relative min-h-0 flex-1">
                  <RuntimeBottomPadding padding={footerHeight}>
                    {(runtimePadding) => (
                      <div
                        {...runtimePadding}
                        className={twJoin(
                          "absolute inset-0 flex scrollbar-transparent flex-col-reverse overflow-y-auto px-space-lg pt-space-lg",
                          showEmptyState && "justify-center",
                        )}
                      >
                        {showEmptyState && (
                          <section className="mx-auto flex w-full max-w-sm flex-col gap-space-lg py-space-sm">
                            {hasSuggestions && (
                              <Stack gap="sm">
                                {filteredSuggestions.map((suggestion) => (
                                  <AskNobiSuggestionButton
                                    key={suggestion}
                                    onClick={() => handleSuggestion(suggestion)}
                                  >
                                    {suggestion}
                                  </AskNobiSuggestionButton>
                                ))}
                              </Stack>
                            )}
                          </section>
                        )}
                        <AskNobiChatFeed chat={chat} onSelectFollowUp={handleSuggestion} />
                      </div>
                    )}
                  </RuntimeBottomPadding>

                  <footer
                    ref={footerRef}
                    className="absolute inset-x-0 bottom-0 z-raised flex flex-col gap-card-compact px-space-lg py-space-md backdrop-blur-xs"
                  >
                    <AskNobiChatInput
                      value={inputValue}
                      onChange={setInputValue}
                      onSubmit={handleSend}
                      disabled={chat.state !== "idle"}
                      placeholder={t("component.ask-nobi-modal.question-input.project-placeholder")}
                      notice={topBar}
                    />
                    <Text as="p" variant="overline" tone="secondary" align="center" overflow="wrap">
                      {t("page.ask-nobi-chat.disclaimer")}
                    </Text>
                  </footer>
                </div>
              </div>
            </ArkDialog.Content>
          </ArkDialog.Positioner>
        </ArkPortal>
      </ArkDialog.Root>

      {conversationId && (
        <AskNobiNegativeFeedbackModal
          isOpen={enrichmentModalOpen}
          onClose={() => {
            enrichmentModalOpenHandlers.setFalse();
            setFeedbackState("submitted");
          }}
          conversationId={conversationId}
          onRated={() => setFeedbackState("submitted")}
        />
      )}
    </>
  );
}
